import { useEffect, useRef, useState } from "react";
import type { CatalogRangeFilter } from "@apex/shared";
import { cn } from "@apex/shared";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";

export interface RangeFilterPopoverProps {
  filter: CatalogRangeFilter;
  /** Current active range, if any. */
  value: { min: number; max: number } | null;
  /** Called when the user clicks Apply with valid bounds. */
  onApply: (range: { min: number; max: number }) => void;
  /** Called when the user clicks Clear. */
  onClear: () => void;
  /** Called when the popover should close (Escape, outside click, after Apply). */
  onClose: () => void;
  className?: string;
}

/**
 * Range filter popover — dual-handle slider built on two native range inputs
 * (avoids a Radix dependency the widget bundle would otherwise have to ship)
 * with synchronised min/max number inputs and a unit suffix. Apply commits
 * the bounds; Clear removes the active range.
 *
 * The slider implementation: two overlapping <input type="range"> elements,
 * z-index ordered so each handle remains independently grabbable. The track
 * fill is a positioned div between the two thumb positions. This is the
 * standard accessible pattern that doesn't need a third-party library.
 */
export function RangeFilterPopover({
  filter,
  value,
  onApply,
  onClear,
  onClose,
  className,
}: RangeFilterPopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Local "draft" state — only commits to the parent on Apply.
  const [draftMin, setDraftMin] = useState<number>(value?.min ?? filter.min);
  const [draftMax, setDraftMax] = useState<number>(value?.max ?? filter.max);

  // Sync from props when the popover re-opens with a different active value.
  useEffect(() => {
    setDraftMin(value?.min ?? filter.min);
    setDraftMax(value?.max ?? filter.max);
  }, [value?.min, value?.max, filter.min, filter.max]);

  // Click-outside to close. We detect by checking that the click target is
  // outside the popover container.
  useEffect(() => {
    function onDocumentMouseDown(e: MouseEvent) {
      const node = containerRef.current;
      if (!node) return;
      // Shadow-DOM-safe variant of `node.contains(e.target)` — see B2 note
      // in SelectFilterPopover for the full reasoning. Keeps slider/input
      // interactions from collapsing the popover when the widget runs
      // inside `<apex-widget>`.
      const path = typeof e.composedPath === "function" ? e.composedPath() : [];
      if (path.includes(node)) return;
      if (node.contains(e.target as Node)) return;
      onClose();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("mousedown", onDocumentMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  function handleApply() {
    // Normalise — swap if the user inverted them, clamp to filter bounds.
    let min = Math.max(filter.min, Math.min(draftMin, draftMax));
    let max = Math.min(filter.max, Math.max(draftMin, draftMax));
    if (Number.isNaN(min)) min = filter.min;
    if (Number.isNaN(max)) max = filter.max;
    onApply({ min, max });
    onClose();
  }

  function handleClear() {
    setDraftMin(filter.min);
    setDraftMax(filter.max);
    onClear();
    onClose();
  }

  // Slider track-fill positioning: percent of the (max - min) span.
  const span = filter.max - filter.min || 1;
  const fillStart = ((draftMin - filter.min) / span) * 100;
  const fillEnd = ((draftMax - filter.min) / span) * 100;

  const unit = filter.unit ?? "";

  return (
    <div
      ref={containerRef}
      data-testid="apex-range-popover"
      role="dialog"
      aria-label={`${filter.displayName} range`}
      className={cn(
        "apex-absolute apex-top-full apex-left-0 apex-mt-2 apex-z-30",
        "apex-w-[20rem] apex-rounded-md apex-border apex-border-border apex-bg-background apex-shadow-lg",
        "apex-p-4",
        className
      )}
    >
      <div className="apex-flex apex-items-center apex-justify-between apex-mb-3">
        <p className="apex-text-sm apex-font-semibold apex-text-foreground">
          {filter.displayName}
          {unit && (
            <span className="apex-text-xs apex-font-normal apex-text-muted-foreground apex-ml-1">
              ({unit})
            </span>
          )}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="apex-rounded apex-p-1 hover:apex-bg-muted apex-text-muted-foreground"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      {/* Dual-handle range slider */}
      <div className="apex-relative apex-h-6 apex-mb-4 apex-mx-1">
        {/* Track */}
        <div
          aria-hidden="true"
          className="apex-absolute apex-top-1/2 apex-left-0 apex-right-0 apex-h-1 apex--translate-y-1/2 apex-rounded-full apex-bg-muted"
        />
        {/* Track fill (between the two handles) */}
        <div
          aria-hidden="true"
          className="apex-absolute apex-top-1/2 apex-h-1 apex--translate-y-1/2 apex-rounded-full apex-bg-primary"
          style={{
            left: `${fillStart}%`,
            right: `${100 - fillEnd}%`,
          }}
        />
        {/* Min handle */}
        <input
          type="range"
          min={filter.min}
          max={filter.max}
          step={filter.step}
          value={draftMin}
          onChange={(e) => {
            const next = Number(e.target.value);
            setDraftMin(Math.min(next, draftMax));
          }}
          aria-label={`${filter.displayName} minimum`}
          className="apex-range-thumb apex-absolute apex-inset-0 apex-w-full apex-h-full apex-appearance-none apex-bg-transparent apex-pointer-events-none"
          style={{ zIndex: draftMin > filter.max - 1 ? 4 : 3 }}
        />
        {/* Max handle */}
        <input
          type="range"
          min={filter.min}
          max={filter.max}
          step={filter.step}
          value={draftMax}
          onChange={(e) => {
            const next = Number(e.target.value);
            setDraftMax(Math.max(next, draftMin));
          }}
          aria-label={`${filter.displayName} maximum`}
          className="apex-range-thumb apex-absolute apex-inset-0 apex-w-full apex-h-full apex-appearance-none apex-bg-transparent apex-pointer-events-none"
          style={{ zIndex: 4 }}
        />
      </div>

      {/* Min/Max number inputs */}
      <div className="apex-grid apex-grid-cols-2 apex-gap-3 apex-mb-4">
        <div className="apex-flex apex-flex-col apex-gap-1">
          <label
            htmlFor={`${filter.id}-min-input`}
            className="apex-text-xs apex-font-medium apex-text-muted-foreground"
          >
            Min
          </label>
          <div className="apex-relative">
            <Input
              id={`${filter.id}-min-input`}
              type="number"
              min={filter.min}
              max={filter.max}
              step={filter.step}
              value={draftMin}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setDraftMin(filter.min);
                  return;
                }
                const next = Number(raw);
                if (Number.isNaN(next)) return;
                setDraftMin(next);
              }}
              className={cn(unit && "apex-pr-8")}
            />
            {unit && (
              <span
                aria-hidden="true"
                className="apex-absolute apex-right-3 apex-top-1/2 apex--translate-y-1/2 apex-text-xs apex-text-muted-foreground"
              >
                {unit}
              </span>
            )}
          </div>
        </div>
        <div className="apex-flex apex-flex-col apex-gap-1">
          <label
            htmlFor={`${filter.id}-max-input`}
            className="apex-text-xs apex-font-medium apex-text-muted-foreground"
          >
            Max
          </label>
          <div className="apex-relative">
            <Input
              id={`${filter.id}-max-input`}
              type="number"
              min={filter.min}
              max={filter.max}
              step={filter.step}
              value={draftMax}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setDraftMax(filter.max);
                  return;
                }
                const next = Number(raw);
                if (Number.isNaN(next)) return;
                setDraftMax(next);
              }}
              className={cn(unit && "apex-pr-8")}
            />
            {unit && (
              <span
                aria-hidden="true"
                className="apex-absolute apex-right-3 apex-top-1/2 apex--translate-y-1/2 apex-text-xs apex-text-muted-foreground"
              >
                {unit}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="apex-flex apex-items-center apex-justify-between apex-gap-2">
        <Button variant="ghost" size="sm" onClick={handleClear}>
          Clear
        </Button>
        <Button size="sm" onClick={handleApply}>
          Apply
        </Button>
      </div>
    </div>
  );
}
